import {
    Injectable, inject
} from "@angular/core";
import {
    setOptions, importLibrary
} from "@googlemaps/js-api-loader";
import {
    Observable, catchError, from, map, of, switchMap
} from "rxjs";

import {
    Country, GeoPoint
} from "@shopliftr/common-js/shared";

import { ICommonConfig } from "../../application/interfaces/common-config";
import { COMMON_APP_CONFIG } from "../../application/constants/app-tokens";

/**
 * Third-Party Client Service for the Google Maps JavaScript API.
 *
 * Wraps `@googlemaps/js-api-loader` so consumers never configure the API key or touch the
 * global `google` namespace to load it, and exposes geocoding as a typed call: no `google.maps.*`
 * type appears in a public signature.
 *
 * @example
 * ```typescript
 * private readonly _googleMapsClient = inject(GoogleMapsClientService);
 *
 * this._googleMapsClient.geoCode$("1 Main St", "Winnetka", "IL", Country.UnitedStates, "60093")
 *     .subscribe((location) => location ? this._setLocation(location) : this._warnUnlocated());
 * ```
 */
@Injectable({
    providedIn: "root"
})
export class GoogleMapsClientService {

    private readonly _appConfig = inject<ICommonConfig>(COMMON_APP_CONFIG);

    constructor() {
        setOptions({
            key: this._appConfig.googleMapsApiKey,
            libraries: [
                "core",
                "marker",
                "maps",
                "geocoding"
            ],
            v: "weekly"
        });
    }

    /**
     * Loads the `core`, `maps`, `marker` and `geocoding` libraries. Safe to call repeatedly —
     * the loader deduplicates requests.
     */
    load(): Promise<Array<unknown>> {

        return Promise.all([
            importLibrary("core"),
            importLibrary("maps"),
            importLibrary("marker"),
            importLibrary("geocoding")
        ]);
    }

    /**
     * Geocodes a street address into a GeoPoint.
     *
     * Loads the Google Maps API first. Emits `undefined` when the address cannot be located or the
     * geocoder rejects; never raises a notification — the caller decides what to tell the user.
     *
     * @param address - Street address, e.g. "1 Main St"
     * @param city - City the address is in
     * @param state - State or province code
     * @param country - Restricts results to this country
     * @param zip - ZIP or postal code
     * @returns The first result's location, or `undefined`
     */
    geoCode$(address: string, city: string, state: string, country: Country, zip: string): Observable<GeoPoint | undefined> {

        return from(this.load()).pipe(
            switchMap(() => new google.maps.Geocoder().geocode({
                address: [address, city, state, zip].join(", "),
                componentRestrictions: { country }
            })),
            map((response) => {

                const location = response.results?.at(0)?.geometry?.location;
                return location ? GeoPoint.deserialize({
                    lat: location.lat(),
                    lon: location.lng()
                }) : undefined;
            }),
            catchError(() => of(undefined))
        );
    }
}
